import { z } from 'zod';

export const menuItemSchema = z.object({
  menuItemId: z.string(),
  recipeId: z.string(),
  recipeName: z.string(),
  category: z.string(),
  price: z.number().optional(),
  isAvailable: z.boolean(),
  allergens: z.array(z.string()),
  dietaryFlags: z.array(z.string()),
  sortOrder: z.number(),
});

export const menuScheduleSchema = z.object({
  date: z.string(),
  mealPeriod: z.enum(['breakfast', 'lunch', 'dinner', 'all-day']),
  expectedPax: z.number(),
  actualPax: z.number().optional(),
  notes: z.string().optional(),
});

export const menuSchema = z.object({
  menuId: z.string(),
  siteId: z.string(),
  venueId: z.string(),
  venueName: z.string(),
  name: z.string(),
  description: z.string().optional(),
  type: z.enum(['breakfast', 'lunch', 'dinner', 'all-day', 'special']),
  status: z.enum(['draft', 'active', 'archived']),
  effectiveFrom: z.string(),
  effectiveTo: z.string().optional(),
  items: z.array(menuItemSchema),
  totalItems: z.number(),
  defaultPax: z.number().optional(),
  venueCapacity: z.number().optional(),
  schedule: z.array(menuScheduleSchema).optional(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type MenuItem = z.infer<typeof menuItemSchema>;
export type MenuSchedule = z.infer<typeof menuScheduleSchema>;
export type Menu = z.infer<typeof menuSchema>;
export type MenuType = Menu['type'];
export type MenuStatus = Menu['status'];
export type MealPeriod = MenuSchedule['mealPeriod'];

export const menusResponseSchema = z.object({
  data: z.array(menuSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number() 
  }),
  meta: z.object({
    siteId: z.string(),
    totalPax: z.number().optional(),
    totalCapacity: z.number().optional(),
  }),
});

export type MenusResponse = z.infer<typeof menusResponseSchema>;

export const MENU_TYPE_LABELS: Record<MenuType, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  'all-day': 'All Day',
  special: 'Special Event',
};

export const MENU_STATUS_LABELS: Record<MenuStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  archived: 'Archived',
};

export const MENU_STATUS_COLORS: Record<MenuStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  active: 'bg-green-100 text-green-800',
  archived: 'bg-red-100 text-red-800',
};
